/**
 * Lógica pura de la «Hoja de selección con buscador» (H76): búsqueda sin
 * acentos ni mayúsculas con el tramo que coincide (para resaltarlo) y
 * filtrado de las secciones. Sirve para categorías y, más adelante, para
 * beneficiarios y cuentas.
 */

/** Una fila elegible de la hoja. */
export type FilaSeleccion = {
  id: string;
  nombre: string;
  /** Emoji de la cajita; sin él, la cajita lleva la inicial. */
  emoji?: string | null;
  /** Segunda línea (p. ej. «La última vez con Mercadona»). */
  detalle?: string;
  /** Texto extra por el que también se busca (p. ej. el grupo). */
  buscarEn?: string;
  /** Segunda línea al buscar (p. ej. el grupo de la categoría). */
  detalleBusqueda?: string;
  /** Fila resaltada (la sugerida). */
  destacada?: boolean;
};

export type SeccionSeleccion = {
  id: string;
  titulo: string;
  icono?: 'compass' | 'clock';
  /** Número a la derecha de la cabecera (las filas del grupo). */
  contador?: number;
  filas: FilaSeleccion[];
  /** El título solo nombra la sección para los lectores de pantalla. */
  sinCabecera?: boolean;
  /**
   * Atajos (Sugerida, Recientes): repiten filas de otras secciones, así que
   * al buscar no cuentan.
   */
  atajo?: boolean;
};

/** Tramo `[inicio, fin)` del texto original que coincide con la búsqueda. */
export type Tramo = { inicio: number; fin: number };

export type Resultado = { fila: FilaSeleccion; tramo: Tramo | null };

/** Quita acentos y diacríticos (à → a, ñ → n) y pasa a minúsculas. */
export function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * Busca `consulta` en `texto` sin distinguir acentos ni mayúsculas y
 * devuelve el tramo del texto ORIGINAL que coincide (los índices se
 * conservan aunque «à» se descomponga en dos caracteres).
 */
export function buscarCoincidencia(
  texto: string,
  consulta: string,
): Tramo | null {
  const buscada = normalizar(consulta.trim());
  if (!buscada) {
    return null;
  }
  let plano = '';
  // Para cada carácter de `plano`, en qué índice del original empieza el
  // carácter del que sale y en cuál acaba.
  const desde: number[] = [];
  const hasta: number[] = [];
  let i = 0;
  for (const caracter of texto) {
    const n = normalizar(caracter);
    for (let k = 0; k < n.length; k++) {
      desde.push(i);
      hasta.push(i + caracter.length);
    }
    plano += n;
    i += caracter.length;
  }
  const pos = plano.indexOf(buscada);
  if (pos < 0) {
    return null;
  }
  return { inicio: desde[pos], fin: hasta[pos + buscada.length - 1] };
}

/**
 * Filas que coinciden con la búsqueda, en el orden de las secciones, sin
 * los atajos y sin repetir. Coincide el nombre o el texto extra
 * (`buscarEn`); el tramo resaltado es solo el del nombre.
 */
export function filtrarSecciones(
  secciones: SeccionSeleccion[],
  consulta: string,
): Resultado[] {
  const vistas = new Set<string>();
  const resultados: Resultado[] = [];
  if (!normalizar(consulta.trim())) {
    return resultados;
  }
  for (const seccion of secciones) {
    if (seccion.atajo) {
      continue;
    }
    for (const fila of seccion.filas) {
      if (vistas.has(fila.id)) {
        continue;
      }
      const tramo = buscarCoincidencia(fila.nombre, consulta);
      if (
        tramo ||
        (fila.buscarEn && buscarCoincidencia(fila.buscarEn, consulta))
      ) {
        vistas.add(fila.id);
        resultados.push({ fila, tramo });
      }
    }
  }
  return resultados;
}

/** Primera fila que se ve sin buscar (la que elige Intro). */
export function primeraFila(
  secciones: SeccionSeleccion[],
): FilaSeleccion | null {
  for (const seccion of secciones) {
    if (seccion.filas.length > 0) {
      return seccion.filas[0];
    }
  }
  return null;
}
